import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

import { AuthProvider } from './lib/auth';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import CommandPalette from './components/CommandPalette';
import WelcomeTour from './components/WelcomeTour';

import Landing from './pages/Landing';
import Marketplace from './pages/Marketplace';
import VehicleDetail from './pages/VehicleDetail';
import Rides from './pages/Rides';
import Events from './pages/Events';
import Pricing from './pages/Pricing';
import Download from './pages/Download';
import Sell from './pages/Sell';
import HostEvent from './pages/HostEvent';
import SignIn from './pages/SignIn';
import Terms from './pages/Terms';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import Billing from './pages/Billing';
import Maps from './pages/Maps';
import Watchlist from './pages/Watchlist';
import Profile from './pages/Profile';
import UserProfile from './pages/UserProfile';
import AuthCallback from './pages/AuthCallback';
import NotFound from './pages/NotFound';

/**
 * Router-level check for the OAuth callback fragment (must be synchronous).
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function AppRouter() {
  const location = useLocation();
  const [cmdkOpen, setCmdkOpen] = React.useState(false);

  // Global ⌘K / Ctrl+K hotkey
  React.useEffect(() => {
    const onKey = (e) => {
      const isCmdK = (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (isCmdK) {
        e.preventDefault();
        setCmdkOpen((v) => !v);
      } else if (e.key === '/' && !cmdkOpen) {
        // Also open on plain "/" if not typing in an input
        const t = e.target;
        const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
        if (!typing) { e.preventDefault(); setCmdkOpen(true); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmdkOpen]);

  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <>
      <ScrollToTop />
      <Navbar onOpenCommand={() => setCmdkOpen(true)} />
      <CommandPalette open={cmdkOpen} onOpenChange={setCmdkOpen} />
      <WelcomeTour />
      <main data-testid="main-content" key={location.pathname} className="am-route-in">
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/vehicles/:id" element={<VehicleDetail />} />
          <Route path="/rides" element={<Rides />} />
          <Route path="/events" element={<Events />} />
          <Route path="/maps" element={<Maps />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/download" element={<Download />} />
          <Route path="/sell" element={<Sell />} />
          <Route path="/host-event" element={<HostEvent />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/watchlist" element={<Watchlist />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/users/:userId" element={<UserProfile />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <Footer />
    </>
  );
}

function App() {
  React.useEffect(() => {
    if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}

export default App;
